import { Box, Button, IconButton, styled, Tooltip, Typography } from '@mui/material'
import * as React from 'react'
import { colors } from '../../constants/colors'
import { BlueBox, HBox, InfoBox, SquareButton, VBox } from '../../elements/basic-elements'
import { ScrollCard } from '../../elements/cards'
import ArrowRight from '../../images/icons/arrow-right'

import { ArrowForward, Check, Download, InfoOutlined, OpenInNew } from '@mui/icons-material'
import { graphql, Link, useStaticQuery } from 'gatsby'
import TodoIconFilled from '../../images/icons/todo-icon-filled'
import { authState } from '../../state/auth-state'

import ConfirmActionCard from '../confirm-action-card'
import FullScreenModal from '../full-screen-modal'
import CenteredModal from '../modals/centered-modal'
import { ShareButton } from '../share-button'

import {
  blocksToText,
  getLocationTagsFromSanityLocation,
  getRepresentativeTagsFromOutreach,
  getUniqueCobenefits,
  sanityString
} from '../../async/sanity/utils'
import TodoIcon from '../../images/icons/todo-icon'
import { PillSmall } from '../../styles/styled-components/inputs/pills'
import {
  DEFAULT_POSITION_ICON_URL,
  getPositionIconSrc
} from '../../utilities/apl-position-assets'
import { download, getDomain, isInBrowser } from '../../utilities/browser'
import { hasValue } from '../../utilities/data'
import { extractSimpleUrl } from '../../utilities/string'
import type { AplPosition } from '../../types/apl-position-types'
import GetMembershipModal from '../get-membership-modal'
import ImpactScoreGlobes from '../impact-score-globes'
import TooltipTitle from '../tooltip-title'
import RepCard, { RepCardData } from './rep-card'
import Representatives from './representatives'
import ResultCardCta from './result-card-cta'

export type ResultCardData = {
  location: Queries.Maybe<Queries.SanityLocation>
  organization: Queries.Maybe<Queries.SanityOrganization>
  title: Queries.Maybe<string>
  rank: Queries.Maybe<number>
  description?: Queries.Maybe<readonly Queries.Maybe<Queries.SanityBlock>[]>
  tags: Queries.Maybe<readonly Queries.Maybe<Queries.SanityTag>[]>
  nouns: Queries.Maybe<readonly Queries.Maybe<Queries.SanityNoun>[]>
  materials?: Queries.Maybe<Queries.SanityActionMaterials>
  contributions?: Queries.Maybe<ReadonlyArray<Queries.Maybe<Queries.SanityActionContribution>>>
  slug: Queries.Maybe<Queries.SanitySlug>
  categories: Queries.Maybe<readonly Queries.Maybe<Queries.SanityCategory>[]>
  outreach: Queries.Maybe<Queries.SanityOutreach>
  donation?: Queries.Maybe<Queries.SanityEveryDonation>
  actionUrl?: string
  url: Queries.Maybe<string>
  shareUrl?: string
  position_ids?:
    | Queries.Maybe<ReadonlyArray<Queries.Maybe<Queries.SanityPosition>>>
    | AplPosition[]
    | null
}

type ResultCardFooterType = 'list' | 'detail'

const labelLimit = 5

const getPositionComputedIconSrc = (position: AplPosition) =>
  getPositionIconSrc(position) || DEFAULT_POSITION_ICON_URL

const handlePositionIconError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  const target = event.currentTarget
  if (!target.src.endsWith(DEFAULT_POSITION_ICON_URL)) {
    target.src = DEFAULT_POSITION_ICON_URL
  }
}

const normalizePositions = (positions: unknown): AplPosition[] | undefined => {
  if (!positions) return
  if (Array.isArray(positions)) {
    return positions.filter(Boolean) as AplPosition[]
  }
  if (Array.isArray((positions as { nodes?: unknown[] }).nodes)) {
    return ((positions as { nodes?: unknown[] }).nodes || []).filter(Boolean) as AplPosition[]
  }
  if (Array.isArray((positions as { edges?: { node?: unknown }[] }).edges)) {
    return ((positions as { edges?: { node?: unknown }[] }).edges || [])
      .map(edge => edge?.node)
      .filter(Boolean) as AplPosition[]
  }
}

const extractPositions = (source: unknown): AplPosition[] | undefined => {
  if (!source) return
  const record = source as {
    position_ids?: unknown
  }

  return normalizePositions(record.position_ids)
}

const getPositionTitle = (position: AplPosition) =>
  position.name || position.title || position.label || 'Untitled position'

const getPositionSubtitle = (position: AplPosition) => {
  if (position.metric) return position.metric
  return ''
}

const getPositionUrl = (position: AplPosition) => position.url || position.link || undefined

const ResultCard: React.FC<{
  data: ResultCardData
  footer: ResultCardFooterType
  consolidate?: boolean
  onDetailClick?: Function
}> = ({ data, footer, consolidate, onDetailClick }) => {
  // TODO: connect this w/ user's profile if they are logged in

  // active action

  const [confirmActionModalActive, showConfirmActionModal] = React.useState(false)

  // action representative

  const [repModalActive, showRepModal] = React.useState(false)
  const [activeRep, setActiveRep] = React.useState<RepCardData>()

  React.useEffect(() => {
    if (activeRep) {
      showRepModal(true)
    }
  }, [activeRep])

  React.useEffect(() => {
    if (!repModalActive) {
      setTimeout(() => {
        setActiveRep(undefined)
      }, 500)
    }
  }, [repModalActive])

  // user data

  const { account, actionsTaken, favorites, updateFavorite } = authState()

  // action presentation states

  const actionIsCompleted = Boolean(
    actionsTaken && actionsTaken.find(({ action_slug }) => action_slug == data.slug?.current)
  )
  const actionIsFavorited = favorites && favorites.indexOf(data.slug?.current) > -1

  // card attributes

  const orgImage = data.organization?.logo?.asset?.url

  const locationTags = getLocationTagsFromSanityLocation(data.location) || []
  const representativeTags = data.outreach?.isOutreach
    ? getRepresentativeTagsFromOutreach(data.outreach)
    : []
  const labelTags = [...locationTags, ...representativeTags]?.filter(value => hasValue(value.label))
  const truncateLabels = labelTags.length > labelLimit

  const { isOutreach, script } = data.outreach || {}

  // account creation
  const uniqueCobenefits =
    data.nouns && getUniqueCobenefits(data.nouns.map(({ cobenefits }) => cobenefits).flat())

  const uniqueCobenefitsCapped = consolidate
    ? uniqueCobenefits && [...uniqueCobenefits].splice(0, 4)
    : uniqueCobenefits
  const cobenniesToShow = uniqueCobenefitsCapped

  const [createAccountModal, showCreateAccountModal] = React.useState(false)
  const positions = React.useMemo(() => extractPositions(data), [data])

  const consolidatedPositions = positions ? positions.slice(0, 1) : []

  const { allSanityUpgradeModal } = useStaticQuery<Queries.SanityUpgradeModalQuery>(graphql`
    query SanityUpgradeModal {
      allSanityUpgradeModal {
        nodes {
          ...upgradeModal
        }
      }
    }
  `)

  // this is an annoying getaround we have to do since querying by slug
  // seems to break Gatsby
  const sanityUpgradeModal = allSanityUpgradeModal?.nodes?.find(
    ({ slug }) => slug.current === 'favorite-action-upgrade-modal'
  )
  // Components
  const TodoButton = React.useCallback(
    () => (
      <SquareButton
        variant="contained"
        color="primary"
        disableElevation
        onClick={() => {
          if (!account) {
            // show create an account warning
            showCreateAccountModal(true)
          } else {
            updateFavorite(data.slug?.current, !actionIsFavorited)
          }
        }}
      >
        {actionIsFavorited ? <TodoIconFilled /> : <TodoIcon />}
      </SquareButton>
    ),
    [account, actionIsFavorited]
  )
  const ActionTakenButton = React.useCallback(
    () =>
      !actionIsCompleted && (
        <SquareButton
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => {
            showConfirmActionModal(true)
          }}
        >
          <Check />
        </SquareButton>
      ),
    [actionIsCompleted]
  )

  const navigateToLink = React.useCallback(() => {
    data.url && window.open(data.url, '_blank')
  }, [data.url])

  const onConfirm = React.useCallback(() => {
    if (!actionIsCompleted && !isOutreach) {
      showConfirmActionModal(true)
    }
  }, [actionIsCompleted, isOutreach])

  const showRepresentatives =
    !consolidate &&
    data.outreach?.isOutreach &&
    data.outreach?.targets.length > 0 &&
    hasValue(data.outreach?.script)

  // Show the primary CTA unless this is a representative outreach card
  const showPrimaryCta = !showRepresentatives

  return (
    // @ts-expect-error: existing type issue
    <ScrollCard elevation={0}>
      {() => ({
        content: (
          <React.Fragment>
            <VBox gap={2} alignItems="flex-start">
              <HBox gap={1} width="100%" alignItems="flex-start">
                <HBox flex={1} flexWrap="wrap" gap={0.5}>
                  {labelTags?.length > 0 ? (
                    <>
                      {(truncateLabels ? [...labelTags.slice(0, labelLimit)] : labelTags).map(
                        (tag, t) => (
                          <LocationBox key={t} type={tag.type}>
                            <Typography variant="body2">{tag.label}</Typography>
                          </LocationBox>
                        )
                      )}
                      {truncateLabels && (
                        <Tooltip
                          placement="top"
                          title={
                            <HBox flexWrap="wrap" gap={0.5}>
                              {[...labelTags.slice(labelLimit, labelTags.length)].map((tag, t) => (
                                <LocationBox key={t} type={tag.type}>
                                  <Typography variant="body2">{tag.label}</Typography>
                                </LocationBox>
                              ))}
                            </HBox>
                          }
                          PopperProps={{
                            disablePortal: true
                          }}
                          sx={{
                            display: 'inline-block'
                          }}
                        >
                          <LocationBox type="other">
                            <Typography variant="body2">
                              and {labelTags.length - labelLimit} more
                            </Typography>
                          </LocationBox>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <LocationBox>
                      <Typography variant="body2">Global</Typography>
                    </LocationBox>
                  )}
                </HBox>

                <HBox gap={0} flex={0} sx={{ mt: theme => theme.spacing(-0.5) }}>
                  {data.url && (
                    <Tooltip
                      placement="right"
                      title={
                        <TooltipTitle description={'Navigate to ' + extractSimpleUrl(data.url)} />
                      }
                    >
                      <IconButton color="secondary" onClick={navigateToLink}>
                        <OpenInNew />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{ mt: theme => theme.spacing(-0.25) }}>
                    <ShareButton
                      text={data.shareUrl || getDomain() + '/action/' + data.slug?.current}
                    />
                  </Box>
                </HBox>
              </HBox>
              <Lockup gap={1}>
                {orgImage && (
                  <ImageBox>
                    <img
                      style={{
                        maxWidth: 200,
                        maxHeight: 100,
                        objectFit: 'contain'
                      }}
                      src={orgImage}
                    />
                  </ImageBox>
                )}
                <HBox mb={1.5} gap={1} alignItems="center">
                  <Typography color="textPrimary" variant="h3">
                    {data.title}
                  </Typography>
                </HBox>
              </Lockup>
            </VBox>
            <VBox flex={1} justifyContent={consolidate ? 'flex-start' : 'center'} gap={1}>
              <VBox gap={2}>
                {data.rank && <ImpactScoreGlobes includeLabel rank={data.rank} />}
                {/* @ts-expect-error: existing type issue */}
                {blocksToText(data.description).map((block, b) => (
                  <Box mb={1.2} key={b}>
                    <Typography variant="body2" color="textPrimary">
                      {block}
                    </Typography>
                  </Box>
                ))}
              </VBox>

              {showRepresentatives && <Representatives action={data} onSelectRep={setActiveRep} />}

              {positions && positions.length > 0 && (
                <VBox gap={1} sx={{ mt: consolidate ? 'auto' : 2, mb: 2, width: '100%' }}>
                  {!consolidate && (
                    <HBox>
                      <Typography color="textPrimary" variant="h6">
                        Positions
                      </Typography>
                      <Tooltip
                        style={{ marginTop: '6px', marginLeft: 'auto' }}
                        enterTouchDelay={0}
                        placement="top"
                        title={
                          <TooltipTitle
                            description={[
                              `Positions are specific policy stances connected to this action.`
                            ]}
                          />
                        }
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ transform: `translateY(-0.25em)`, color: 'currentcolor' }}
                        />
                      </Tooltip>
                    </HBox>
                  )}
                  {consolidate ? (
                    <>
                      <PositionsCompactRow>
                        {consolidatedPositions.map((position, p) => {
                          const title = getPositionTitle(position)
                          const subtitle = getPositionSubtitle(position)
                          const computedIconUrl = getPositionComputedIconSrc(position)
                          return (
                            <PositionCompactCard key={position._id || position.slug?.current || p}>
                              <PositionPill>Position</PositionPill>
                              <PositionHeaderRow>
                                {/* <PositionPrimaryIcon>
                                  <img
                                    src={POSITION_GENERIC_ICON_URL}
                                    alt=""
                                    onError={handlePositionIconError}
                                  />
                                </PositionPrimaryIcon> */}
                                {computedIconUrl && (
                                    <PositionComputedIcon sx={{ alignSelf: 'flex-end' }}>
                                      <img
                                        src={computedIconUrl}
                                        alt=""
                                        onError={handlePositionIconError}
                                      />
                                    </PositionComputedIcon>
                                  )}
                                <PositionTitle variant="body1" color="textPrimary" $clampLines={2}>
                                  {title}
                                </PositionTitle>
                              </PositionHeaderRow>
                              {(subtitle || computedIconUrl) && (
                                <PositionMetaRow>
                                  {/* {subtitle && (
                                    <PositionSubtitle variant="body2" $clampLines={2}>
                                      {subtitle}
                                    </PositionSubtitle>
                                  )} */}
                                  
                                </PositionMetaRow>
                              )}
                            </PositionCompactCard>
                          )
                        })}
                      </PositionsCompactRow>
                      {/* {remainingPositions > 0 && (
                        <PositionCount>
                          <Typography variant="body2" color="textPrimary">
                            +{remainingPositions} more positions
                          </Typography>
                        </PositionCount>
                      )} */}
                    </>
                  ) : (
                    <>
                      <hr style={{ width: '100%', opacity: 0.6 }} />

                      <PositionsRow>
                        {positions.map((position, p) => {
                          const title = getPositionTitle(position)
                          const subtitle = getPositionSubtitle(position)
                          const computedIconUrl = getPositionComputedIconSrc(position)
                          const positionUrl = getPositionUrl(position)
                          return (
                            <PositionCard key={position._id || position.slug?.current || p}>
                              <PositionPrimaryIcon>
                                {/* <img
                                  src={POSITION_GENERIC_ICON_URL}
                                  alt=""
                                  onError={handlePositionIconError}
                                /> */}
                              </PositionPrimaryIcon>
                              <PositionTitle variant="body1" color="textPrimary" $clampLines={0}>
                                {title}
                              </PositionTitle>
                              {(subtitle || computedIconUrl) && (
                                <PositionMetaRow>
                                  {subtitle && (
                                    <PositionSubtitle variant="body2" $clampLines={0}>
                                      {subtitle}
                                    </PositionSubtitle>
                                  )}
                                  {computedIconUrl && (
                                    <PositionComputedIcon sx={{ alignSelf: 'flex-end', ml: 'auto' }}>
                                      <img
                                        src={computedIconUrl}
                                        alt=""
                                        onError={handlePositionIconError}
                                      />
                                    </PositionComputedIcon>
                                  )}
                                </PositionMetaRow>
                              )}
                              {positionUrl && (
                                <PositionLink
                                  size="small"
                                  onClick={() => {
                                    if (isInBrowser()) {
                                      window.open(positionUrl, '_blank')
                                    }
                                  }}
                                >
                                  <ArrowForward fontSize="small" />
                                </PositionLink>
                              )}
                            </PositionCard>
                          )
                        })}
                      </PositionsRow>
                    </>
                  )}
                </VBox>
              )}

              {cobenniesToShow && cobenniesToShow.length > 0 && (
                <VBox gap={1} sx={{ my: -1, width: '100%' }}>
                  <HBox>
                    <Typography color="textPrimary" variant="h6">
                      Co-Benefits
                    </Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      placement="top"
                      style={{ marginTop: '6px', marginLeft: 'auto' }}
                      title={
                        <TooltipTitle
                          description={[
                            `Co-benefits are the intended positive side effects of a policy`
                          ]}
                        />
                      }
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{ transform: `translateY(-0.25em)`, color: 'currentcolor' }}
                      />
                    </Tooltip>
                  </HBox>
                  <hr style={{ width: '100%', opacity: 0.6 }} />

                  <HBox flexWrap="wrap" gap={0.5}>
                    {cobenniesToShow.map((tag, t) => {
                      return (
                        <Tag key={t}>
                          <Typography variant="body2" color="textPrimary">
                            {tag?.title}
                          </Typography>
                        </Tag>
                      )
                    })}
                    {uniqueCobenefits && uniqueCobenefits.length > cobenniesToShow.length && (
                      <Button sx={{ p: 0 }} onClick={() => onDetailClick && onDetailClick()}>
                        <Tag>
                          <Typography variant="body2" color="textPrimary">
                            ...
                          </Typography>
                        </Tag>
                      </Button>
                    )}
                  </HBox>
                </VBox>
              )}
              {data.materials && data.materials.documents.length > 0 && (
                <VBox>
                  <Typography color="textPrimary" variant="h6">
                    {data.materials.label}
                  </Typography>
                  <InfoBox sx={{ my: 2 }}>
                    {data.materials.documents.map(
                      ({ name, description, file }: Queries.SanityActionMaterial, m) => {
                        const fileUrl = file?.asset?.url
                        const fileName = file?.asset?.originalFilename
                        return (
                          <HBox gap={2} key={m}>
                            {fileUrl && (
                              <SquareButton
                                variant="contained"
                                color={'secondary'}
                                disableElevation
                                onClick={() =>
                                  download(fileUrl, fileName ? sanityString(fileName) : undefined)
                                }
                              >
                                <Download />
                              </SquareButton>
                            )}
                            <VBox>
                              <Typography variant="body1" color="textPrimary">
                                {name}
                              </Typography>
                              <Typography mt={0.5} variant="body2" color="textPrimary">
                                {description}
                              </Typography>
                            </VBox>
                          </HBox>
                        )
                      }
                    )}
                  </InfoBox>
                </VBox>
              )}
            </VBox>

            <FullScreenModal
              open={Boolean(repModalActive)}
              colorScheme="white"
              onClose={() => {
                showRepModal(false)
                showConfirmActionModal(true)
              }}
            >
              {activeRep && (
                <RepCard
                  {...{
                    ...activeRep,
                    action: data.title,
                    script
                  }}
                />
              )}
            </FullScreenModal>
            <CenteredModal
              open={confirmActionModalActive}
              onClose={() => showConfirmActionModal(false)}
              positionAtBottomOnMobile
            >
              <ConfirmActionCard
                action={data}
                isCandidate={isOutreach}
                onClose={() => showConfirmActionModal(false)}
              />
            </CenteredModal>

            <GetMembershipModal
              content={sanityUpgradeModal}
              open={createAccountModal}
              onClose={() => showCreateAccountModal(false)}
            />
          </React.Fragment>
        ),
        footer: (
          <Box>
            {footer === 'list' ? (
              <HBox gap={4} alignItems="flex-end">
                <HBox flexWrap="wrap" gap={0.5} flex={1}>
                  {data.tags?.map((tag, t) => {
                    return (
                      <Tag key={t}>
                        <Typography variant="body2" color="textPrimary">
                          {tag?.title}
                        </Typography>
                      </Tag>
                    )
                  })}
                </HBox>

                <HBox gap={0.5}>
                  <TodoButton />
                  <Link to={data.actionUrl}>
                    <SquareButton
                      variant="contained"
                      color={actionIsCompleted ? 'success' : 'primary'}
                      disableElevation
                      onClick={() => {
                        onDetailClick && onDetailClick()
                      }}
                    >
                      {actionIsCompleted ? (
                        <Check fontSize="large" />
                      ) : (
                        <ArrowRight color={colors.WHITE} />
                      )}
                    </SquareButton>
                  </Link>
                </HBox>
              </HBox>
            ) : (
              <HBox gap={1} width={'100%'} alignItems="stretch">
                <VBox width="100%" gap={1}>
                  {showPrimaryCta && (
                    <ResultCardCta
                      actionIsCompleted={actionIsCompleted}
                      data={data}
                      onClickthrough={onConfirm}
                    />
                  )}
                </VBox>
                <TodoButton />
                <ActionTakenButton />
              </HBox>
            )}
          </Box>
        )
      })}
    </ScrollCard>
  )
}

const locationTagColorMap = {
  federal: colors.ALZARIAN_100,
  state: colors.GEORGIAN_BAY_70,
  city: colors.GREEN_100,
  local: colors.GREEN_100,
  other: colors.GEORGIAN_BAY_30
}
const locationTagTextColorMap = {
  federal: colors.WHITE,
  state: colors.WHITE,
  city: colors.WHITE,
  local: colors.WHITE,
  other: colors.WHITE
}

export const LocationBox = styled(PillSmall)<{ type?: string }>`
  ${({ theme, type }) => `
    color: ${locationTagTextColorMap[type] || theme.palette.primary.contrastText};
    font-size: 13px;
    background: ${
      locationTagColorMap[type]
        ? locationTagColorMap[type]
        : theme.palette.mode == 'dark'
          ? colors.DARK_MODE_50
          : colors.GEORGIAN_BAY_70
    };
    .MuiTypography-root {
      font-size: 13px;
    }
  `}
`

const Tag = styled(BlueBox)``

const ImageBox = styled(Box)`
  ${({ theme }) => `
  width: fit-content;
  border-radius: 16px;
    //  background: ${theme.palette.mode == 'dark' ? 'rgba(0, 0, 0, 0.12)' : 'transparent'};
`}
`
const Lockup = styled(VBox)`
  ${({ theme }) => `
  width: 100%;
    ${theme.breakpoints.down('md')} {
      flex-direction: row;

      img {
        width: 64px;
        height: 64px;
        object-fit: contain;
      }
    }
  `}
`

const PositionPill = styled(Box)`
  ${({ theme }) => `
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  width: fit-content;
  border-radius: 16px;
  padding: 4px 12px;
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  font-family: 'HCo Gotham';
  color: ${theme.palette.mode == 'dark' ? 'currentColor' : 'white'};
  background: linear-gradient(227.53deg, #004660 8.96%, #237D9D 94.13%);
`}
`

const PositionsRow = styled(HBox)`
  ${({ theme }) => `
    width: 100%;
    gap: ${theme.spacing(1.5)};
    overflow-x: auto;
    padding-bottom: ${theme.spacing(2)};
  `}
`

const PositionsCompactRow = styled(HBox)`
  ${({ theme }) => `
    width: 100%;
    gap: ${theme.spacing(1)};
    align-items: center;
    `}
`

const PositionCard = styled(Box)`
  ${({ theme }) => `
    min-width: 220px;
    max-width: 260px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1.5)};
    border-radius: 18px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_70 : colors.BLACK_10};
    align-items: flex-start;
  `}
`

const PositionCompactCard = styled(Box)`
  ${({ theme }) => `
    flex: 1;
    min-width: 0;
    display: flex;
    position: relative;
    flex-direction: column;
    text-align: left;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing(2.25)} ${theme.spacing(2)};
    border-radius: 16px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_70 : colors.GEORGIAN_BAY_10};
  `}
`

const PositionPrimaryIcon = styled(Box)`
  ${() => `
    width: 40px;
    height: 40px;
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
  `}
`

const PositionHeaderRow = styled(HBox)`
  width: 100%;
  align-items: center;
  gap: 10px;
`

const PositionMetaRow = styled(HBox)`
  width: 100%;
  align-items: center;
  gap: 8px;
`

const PositionTitle = styled(Typography)<{ $clampLines?: number }>`
  ${({ $clampLines }) =>
    $clampLines && $clampLines > 0
      ? `
        display: -webkit-box;
        -webkit-line-clamp: ${$clampLines};
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      `
      : `
        display: block;
        overflow: visible;
        text-overflow: unset;
      `}
  font-weight: 600;
`

const PositionSubtitle = styled(Typography)<{ $clampLines?: number }>`
  ${({ $clampLines }) =>
    $clampLines && $clampLines > 0
      ? `
        display: -webkit-box;
        -webkit-line-clamp: ${$clampLines};
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      `
      : `
        display: block;
        overflow: visible;
        text-overflow: unset;
      `}
  color: ${({ theme }) => (theme.palette.mode == 'dark' ? colors.DARK_MODE_10 : colors.BLACK_50)};
  flex: 1;
  min-width: 0;
`

const PositionComputedIcon = styled(Box)`
  ${({ theme }) => `
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 999px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_50 : colors.WHITE};
    color: ${theme.palette.mode == 'dark' ? colors.WHITE : colors.GEORGIAN_BAY_100};
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
  `}
`

const PositionLink = styled(IconButton)`
  ${({ theme }) => `
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_50 : colors.WHITE};
  `}
`

export default ResultCard
